import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAllManufacturers } from '../../store/actions';
import { ChevronRight } from 'lucide-react';
import Header from '../../components/layout/Header';
import Badge, { statusToBadge } from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import useDebouncedValue from '../../customHooks/useDebouncedValue';
import { PARTNER_STATUS, formatINR, initials } from '../../constants/garmentCodes';

export default function BrandManufacturers() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const manufacturers = useSelector((state) => state.manufacturer.manufacturers);
  const loading = useSelector((state) => state.manufacturer.loading);

  useEffect(() => {
    dispatch(getAllManufacturers({ name: debouncedSearch.trim(), status: '' }));
  }, [dispatch, debouncedSearch]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header title="Manufacturers" subtitle={`${manufacturers.length} manufacturers`} />
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="flex items-center gap-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search manufacturers…" className="text-sm border border-[#E5E3DE] rounded-lg px-3 py-1.5 bg-white w-60 outline-none focus:border-[#3D52A0]" />
          {loading && <span className="text-xs text-[#9B9893]">Loading…</span>}
        </div>

        {!loading && manufacturers.length === 0 && (
          <div className="bg-white rounded-xl border border-[#E5E3DE] py-12 text-center text-sm text-[#9B9893]">No manufacturers found</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {manufacturers.map((mfr) => {
            const decisions = mfr.pendingDecisions ?? 0;
            const active = mfr.activeEngagements ?? 0;
            return (
              <div key={mfr.id} className="bg-white rounded-xl border border-[#E5E3DE] p-5 hover:shadow-md transition-shadow group">
                <div className="flex items-center gap-3 mb-4">
                  {mfr.logo ? (
                    <img src={mfr.logo} alt="" className="w-10 h-10 rounded-xl object-cover shrink-0" />
                  ) : (
                    <div className="w-10 h-10 bg-[#1E2A3B] rounded-xl flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-white">{initials(mfr.name)}</span>
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1A1915] truncate">{mfr.name}</p>
                    <p className="text-xs text-[#9B9893]">{mfr.location || '—'}</p>
                  </div>
                  {mfr.status && <div className="ml-auto"><Badge variant={statusToBadge(mfr.status)}>{mfr.status}</Badge></div>}
                </div>
                <div className="grid grid-cols-3 gap-3 text-center border-y border-[#F4F3F0] py-4 mb-4">
                  <div>
                    <p className="text-xs text-[#9B9893]">Balance</p>
                    <p className="text-sm font-bold text-[#1A1915]">{formatINR(mfr.walletBalance)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#9B9893]">Available</p>
                    <p className="text-sm font-bold text-[#1A6B3E]">{formatINR(mfr.availableBalance)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#9B9893]">Decisions</p>
                    <p className={`text-sm font-bold ${decisions > 0 ? 'text-[#C0222B]' : 'text-[#9B9893]'}`}>{decisions}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#9B9893]">{active} engagement{active === 1 ? '' : 's'}</span>
                  <Button
                    variant="ghost"
                    size="xs"
                    iconRight={<ChevronRight size={11} />}
                    onClick={() => navigate(`/brand/engagements?manufacturerId=${mfr.id}`)}
                    className="group-hover:bg-[#EEF0FB] group-hover:text-[#3D52A0]"
                  >
                    View
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
